import React, { useState, useEffect, useRef } from "react";
import Cookies from "universal-cookie";
import { createNotification } from "../../modules/notificationManager";
import { getTwoLetter } from "../../utils/helper";
import arrowDown from "assets/img/Sidebar/sidebar-arrow-down.svg";
import {
  PopupOverlay,
  PopupContainer,
  UserHeaderCard,
  UserInfo,
  UserAvatar,
  UserAvatarInitials,
  UserTextBlock,
  UserName,
  UserEmail,
  LanguageDropdownWrapper,
  LanguageButton,
  LanguageArrow,
  LanguageDropdown,
  LanguageOption,
  Section,
  SectionLabel,
  OrgItem,
  OrgItemInfo,
  OrgBadge,
  OrgName,
  SelectedOrgTick,
  Divider,
  ArchivedHeader,
  ArchivedLink,
  FooterRow,
  FooterButton,
} from "./mobileProfilePopupStyles";

const TickIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#2266EF" />
    <path
      d="M5.5 10L8.5 13L14.5 7"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileProfilePopup = ({
  isOpen,
  onClose,
  organizations,
  selectedOrganization,
  selectOrganization,
  loginProfileCreatedReset,
  profile,
  history,
}) => {
  const cookies = new Cookies();
  const languageDropdownRef = useRef(null);

  const languageList = [
    { label: "English", value: "en" },
    { label: "العربية", value: "ar" },
  ];

  const [activeOrgsList, setActiveOrgsList] = useState([]);
  const [archivedOrgsList, setArchivedOrgsList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(
    localStorage.getItem("selected_organization_id"),
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  const userFullName = localStorage.getItem("user_fullName") || "";
  const userEmail = localStorage.getItem("email") || "";

  const thumbnail = profile?.profileData?.thumbnail;
  const profileColor = profile?.profileData?.color;

  const currentLanguage = cookies.get("languageLevel") || "English";

  // Split orgs into active/archived when organizations list changes
  useEffect(() => {
    if (organizations?.list?.length > 0) {
      const active = [];
      const archived = [];
      organizations.list.forEach(org => {
        if (org.is_active) active.push(org);
        else archived.push(org);
      });
      setActiveOrgsList(active);
      setArchivedOrgsList(archived);
      if (!isInitialized) setIsInitialized(true);
    }
  }, [organizations?.list]);

  // Keep selectedOrgId in sync
  useEffect(() => {
    if (organizations?.selectedOrganization?.id) {
      setSelectedOrgId(organizations.selectedOrganization.id);
    }
  }, [organizations?.selectedOrganization?.id]);

  // Handle outside click for language dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    if (langDropdownOpen && isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [langDropdownOpen, isOpen]);

  const handleLanguageChange = e => {
    cookies.set("language", e.value, { path: "/" });
    cookies.set("languageLevel", e.label, { path: "/" });
    setLangDropdownOpen(false);
    window.location.reload(false);
  };

  const selectOrg = org => {
    localStorage.setItem("selected_organization", org.name);
    localStorage.setItem("selected_organization_id", org.id);
    localStorage.setItem("selected_organization_role", org.role);
    selectOrganization(org);
    if (isInitialized) {
      createNotification("success", `Switched to ${org.name}`, 2000);
    }
    onClose();
  };

  const handleLogout = () => {
    const keys = [
      "access_token", "user_id", "user_role", "viewer_role",
      "user_profile", "user_fullName", "selected_organization",
      "selected_organization_id", "selected_organization_role",
      "selected_organization_plan", "email", "created_at",
      "is_owner", "user_timezone",
    ];
    keys.forEach(k => localStorage.removeItem(k));
    history.push("/auth/login");
    loginProfileCreatedReset();
    onClose();
  };

  const handleProfileSettings = () => {
    history.push("/user/user-profile");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <PopupOverlay isOpen={isOpen} onClick={onClose} />
      <PopupContainer isOpen={isOpen}>
        {/* User header */}
        <UserHeaderCard>
          <UserInfo>
            {thumbnail ? (
              <UserAvatar src={thumbnail} alt={userFullName} />
            ) : (
              <UserAvatarInitials color={profileColor}>
                {userFullName
                  ? userFullName
                      .split(" ")
                      .slice(0, 2)
                      .map(w => w[0])
                      .join("")
                      .toUpperCase()
                  : "?"}
              </UserAvatarInitials>
            )}
            <UserTextBlock>
              <UserName>{userFullName}</UserName>
              <UserEmail>{userEmail}</UserEmail>
            </UserTextBlock>
          </UserInfo>
          <LanguageDropdownWrapper ref={languageDropdownRef}>
            <LanguageButton onClick={() => setLangDropdownOpen(!langDropdownOpen)}>
              {currentLanguage}
              <LanguageArrow src={arrowDown} alt="" />
            </LanguageButton>
            <LanguageDropdown isOpen={langDropdownOpen}>
              {languageList.map((lang, idx) => (
                <LanguageOption
                  key={idx}
                  onClick={() => handleLanguageChange(lang)}
                >
                  {lang.label}
                </LanguageOption>
              ))}
            </LanguageDropdown>
          </LanguageDropdownWrapper>
        </UserHeaderCard>

        {/* Active organizations */}
        {activeOrgsList.length > 0 && (
          <Section>
            <SectionLabel>Organizations</SectionLabel>
            {activeOrgsList.map((org, idx) => {
              const isSelected = org.id === selectedOrgId || org.id === selectedOrganization?.id;
              return (
                <OrgItem
                  key={idx}
                  selected={isSelected}
                  onClick={() => !isSelected && selectOrg(org)}
                >
                  <OrgItemInfo>
                    <OrgBadge bgColor={org.is_active ? org.color : "#9eafc2"}>
                      {getTwoLetter(org.name)}
                    </OrgBadge>
                    <OrgName>
                      {org.name.length < 28 ? org.name : `${org.name.slice(0, 28)}…`}
                    </OrgName>
                  </OrgItemInfo>
                  {isSelected && (
                    <SelectedOrgTick>
                      <TickIcon />
                    </SelectedOrgTick>
                  )}
                </OrgItem>
              );
            })}
          </Section>
        )}

        {/* Archived organizations */}
        {archivedOrgsList.length > 0 && (
          <>
            <Divider />
            <Section>
              <ArchivedHeader>
                <SectionLabel>Archived ({archivedOrgsList.length})</SectionLabel>
                <ArchivedLink
                  href="https://www.fieldservicely.com/help"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  How to unarchive?
                </ArchivedLink>
              </ArchivedHeader>
              {archivedOrgsList.map((org, idx) => {
                const isSelected = org.id === selectedOrgId || org.id === selectedOrganization?.id;
                return (
                  <OrgItem
                    key={idx}
                    selected={isSelected}
                    onClick={() => !isSelected && selectOrg(org)}
                  >
                    <OrgItemInfo>
                      <OrgBadge bgColor="#9eafc2">{getTwoLetter(org.name)}</OrgBadge>
                      <OrgName style={{ color: "#9eafc2" }}>
                        {org.name.length < 28 ? org.name : `${org.name.slice(0, 28)}…`}
                      </OrgName>
                    </OrgItemInfo>
                    {isSelected && (
                      <SelectedOrgTick>
                        <TickIcon />
                      </SelectedOrgTick>
                    )}
                  </OrgItem>
                );
              })}
            </Section>
          </>
        )}

        {/* Footer action buttons */}
        <FooterRow>
          <FooterButton onClick={handleLogout}>Log Out</FooterButton>
          <FooterButton primary onClick={handleProfileSettings}>
            Profile Settings
          </FooterButton>
        </FooterRow>
      </PopupContainer>
    </>
  );
};

export default MobileProfilePopup;
